import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from '../primitives/Icon';
import BrandWordmark from './BrandWordmark';
import NotificationBadge from '../feedback/NotificationBadge';
import { useRequireAuth } from '../../navigation/useRequireAuth';
import { useHasUnreadNotifications } from '../../hooks/useNotificationQueries';
import styles from './AppBar.module.css';

/**
 * AppBar — 외부에는 variant preset 으로 노출하지만 내부 렌더는 단일 return.
 *
 * 각 요소의 출력 여부/내용을 plain flag 로 보유 (back/logo/title/searchInput/homeActions/close).
 * variant 마다 별도 return 블록을 추가하는 대신, 새 룩은 flag 조합으로 표현 → variant
 * 추가 부담 없이 확장 가능.
 *
 * Preset 매핑 (variantToSlots):
 *   home + title=null : 로고만, 우측 알림 (홈 진입)
 *   home + title     : 좌측 텍스트 + 우측 알림 (메뉴 랜딩)
 *   detail           : 좌측 back + 텍스트
 *   search           : 좌측 back + 텍스트 + 우측 알림 (액션 강조용 변형)
 *   popup            : 좌측 텍스트 + 우측 X
 */
export type AppBarVariant = 'home' | 'myPage' | 'detail' | 'search' | 'popup';

interface AppBarProps {
  variant: AppBarVariant;
  title?: string;
  onBackPressed?: () => void;
  /** popup variant 전용 — 우측 X 버튼 클릭 핸들러 */
  onClosePressed?: () => void;
  /**
   * detail variant 의 좌측 chrome 아이콘 swap (2026-05-30):
   *   - 'back' (default): ← (뒤로가기) — 일반 상세
   *   - 'close': ✕ — activity 흐름 종료성이 강한 페이지(예: 결제하기, 큐 흐름)
   * popup variant 는 무시 (우측 ✕ 고정), 그 외 variant 도 무시.
   */
  chromeAction?: 'back' | 'close';
  /**
   * 현재 페이지에 해당하는 우측 액션 아이콘을 active(회색 원형 배경) 상태로 표시.
   * 예: 알림 페이지 `activeAction="notifications"`.
   * active 인 아이콘은 탭해도 재진입하지 않는다 (이미 그 페이지).
   */
  activeAction?: 'search' | 'notifications';
  /**
   * 페이지가 올려보낸 우측 액션 (예: 카드 상세의 공유).
   * variant 별 고정 아이콘(알림·설정·X)보다 **앞에** 놓는다 — 페이지 고유 액션이
   * 공통 액션에 밀려 끝으로 가면 눈에 안 띈다.
   */
  rightAction?: React.ReactNode;
}


// === 내부 slot flag === (각 시각 요소의 출력 상태)
// 우측 아이콘은 4종 독립 flag 로 제어. 새 variant 에서 다른 조합이 필요하면
// variantToSlots 에서 flag 만 갈아끼우면 됨 (prop 증식 없이).
//   - showHomeActions   : Notification (홈 공통)
// 설정(기어) 아이콘은 2026-08-20 에 뺐다 (design 55ec1f1) — 내 정보 [지원] 목록에
// '설정' 이 이미 있어서 같은 곳으로 가는 입구가 한 화면에 둘이었다.
interface Slots {
  showBack: boolean;
  showLogo: boolean;
  showTitle: boolean;
  showSearchInput: boolean;
  showHomeActions: boolean;
  showClose: boolean;
  showPlaceholderRightIcon: boolean;
}

function variantToSlots(variant: AppBarVariant, title: string | undefined): Slots {
  switch (variant) {
    case 'home':
      return {
        showBack: false,
        showLogo: !title,
        showTitle: !!title,
        showSearchInput: false,
        showHomeActions: true,
        showClose: false,
        showPlaceholderRightIcon: false,
      };
    // 마이페이지 전용 — 홈 액션(알림)
    case 'myPage':
      return {
        showBack: false,
        showLogo: false,
        showTitle: !!title,
        showSearchInput: false,
        showHomeActions: true,
        showClose: false,
        showPlaceholderRightIcon: false,
      };
    case 'detail':
      return {
        showBack: true,
        showLogo: false,
        showTitle: !!title,
        showSearchInput: false,
        showHomeActions: false,
        showClose: false,
        showPlaceholderRightIcon: false,
      };
    case 'search':
      return {
        showBack: true,
        showLogo: false,
        showTitle: !!title,
        showSearchInput: false,
        showHomeActions: true,
        showClose: false,
        showPlaceholderRightIcon: false,
      };
    case 'popup':
      return {
        showBack: false,
        showLogo: false,
        showTitle: !!title,
        showSearchInput: false,
        showHomeActions: false,
        showClose: true,
        showPlaceholderRightIcon: false,
      };
  }
}

interface AppBarIconButtonProps {
  iconName: IconName;
  label: string;
  onClick?: () => void;
  /** 우상단 badge overlay — 신규(안 읽은) 알림 유무를 dot 으로만 표시. */
  showBadge?: boolean;
  /** 현재 페이지 액션 — 회색 원형 배경으로 강조. */
  active?: boolean;
}

const AppBarIconButton: React.FC<AppBarIconButtonProps> = ({
  iconName,
  label,
  onClick,
  showBadge = false,
  active = false,
}) => {
  const badge = showBadge ? (
    <span className={styles.badgeOverlay}>
      <NotificationBadge variant="dot" />
    </span>
  ) : null;
  return (
    <button
      type="button"
      className={`${styles.iconButton} ${active ? styles.iconButtonActive : ''}`}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      // active = 현재 페이지 — 재탭 시 같은 activity 를 중복으로 쌓지 않도록 무시.
      onClick={active ? undefined : onClick}
    >
      <Icon name={iconName} size="md" ariaLabel={label} />
      {badge}
    </button>
  );
};

const PlaceholderIcon: React.FC = () => (
  <span className={styles.placeholderIcon} aria-hidden="true">
    {'○'}
  </span>
);

const AppBar: React.FC<AppBarProps> = ({
  variant,
  title,
  onBackPressed,
  onClosePressed,
  chromeAction = 'back',
  rightAction,
  activeAction,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requireAuth } = useRequireAuth();
  const s = variantToSlots(variant, title);
  // home variant 만 알림 아이콘이 노출되므로 그때만 fetch (다른 variant 는 cache hit/empty).
  // react-query 가 동일 key 를 dedup 하므로 HomePage 와 캐시 공유.
  const hasUnread = useHasUnreadNotifications();

  /** 알림 — 메인 탭이므로 activity 를 쌓지 않고 탭 전환. */
  const openNotificationsTab = () => {
    requireAuth(
      { type: "navigate", to: "/notifications", mode: "mainTab" },
      () => navigate("/notifications"),
    );
  };

  // title 스타일: chrome 버튼(back/close) 동반 시 detail 톤(.title, semibold),
  //             없으면 메뉴 랜딩 톤(.logo, bolder).
  const titleClassName = s.showBack || s.showClose ? styles.title : styles.logo;
  // popup (close 만, back 없음) 의 title 은 좌측 끝까지 — Figma 정합
  const titleStyle =
    !s.showBack && s.showClose ? ({ marginLeft: 0 } as const) : undefined;

  return (
    <div className={styles.appBar}>
      <div className={styles.leftGroup}>
        {s.showBack && (
          <button
            type="button"
            className={styles.backButton}
            aria-label={chromeAction === 'close' ? 'Close' : 'Back'}
            onClick={onBackPressed}
          >
            <Icon
              name={chromeAction === 'close' ? 'Close' : 'Back'}
              size="md"
              ariaLabel={chromeAction === 'close' ? 'Close' : 'Back'}
            />
          </button>
        )}
        {/* 워드마크 — 로고 에셋이 나와 글자 대신 이미지로 렌더한다 (2026-08-14).
            배경 밝기에 따라 두 벌이 있고 BrandWordmark 가 골라 준다. */}
        {s.showLogo && <BrandWordmark />}
        {s.showSearchInput && (
          <div className={styles.searchContainer}>
            {/* ⚠ 이모지를 쓰지 않는다 (BRAND §3.6) — 서체 3종에 글리프가 없어
                시스템 컬러 이모지로 폴백돼 도트 서체 인상이 깨진다. */}
            <span className={styles.searchIcon}><Icon name="Search" size="sm" /></span>
            <span className={styles.searchPlaceholder}>
              {t('search.placeholder')}
            </span>
          </div>
        )}
        {s.showTitle && title && (
          <span className={titleClassName} style={titleStyle}>
            {title}
          </span>
        )}
      </div>

      <div className={styles.rightGroup}>
        {rightAction}
        {s.showHomeActions && (
          <AppBarIconButton
            // 활성 표시는 .iconButtonActive(회색 원형 배경)가 이미 한다 — 아이콘은
            // 한 종류만 쓴다. 채워진 종(NotificationActive)은 2026-08-19 에 지웠다.
            iconName="Notification"
            label="Notifications"
            active={activeAction === 'notifications'}
            onClick={openNotificationsTab}
            showBadge={hasUnread}
          />
        )}
        {s.showClose && (
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close"
            onClick={onClosePressed}
          >
            <Icon name="Close" size="md" ariaLabel="Close" />
          </button>
        )}
        {s.showPlaceholderRightIcon && <PlaceholderIcon />}
      </div>
    </div>
  );
};

export default AppBar;
